import styled from 'styled-components';

import { Link, IconText, Tooltip } from 'components/common';
import { formatRange } from 'utils/number';
import SocialMediaLink from 'components/Researcher/Profile/SocialMediaLink';
import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setViewAndResetState } from 'actions/organisation';
import useHorizontalWheel from 'hooks/useHorizontalWheel';
import useOrgProfileLink from 'hooks/useOrgProfileLink';
import { ORGANISATIONPEOPLEVIEW } from 'components/Organisation/helpers';
import Actions from 'components/Organisation/Actions';

import AcquiredIcon from './AcquiredIcon';
import RebrandIcon from './RebrandIcon';
import SubsidiaryIcon from './SubsidiaryIcon';
import FundingTypeIcon from './FundingTypeIcon';

const ProfileContainer = styled.div`
  font-size: 15px;
  line-height: 1.75em;
  color: ${p => p.theme.black};
  flex: 1;
  display: flex;
  align-items: center;
  padding-bottom: 15px;
  min-width: 250px;

  ${p => p.theme.breakpoint.mobile} {
    align-items: normal;
    flex-direction: column;
  }
`;

const ProfileContainerInner = styled.div`
  font-size: 15px;
  line-height: 1.75em;
  color: ${p => p.theme.black};
  display: flex;
  flex-direction: column;
  flex: 1;

  .horizontalScrollable {
    -ms-overflow-style: none;
    scrollbar-width: none;
    overflow-x: auto;
  }

  .horizontalScrollable::-webkit-scrollbar {
    display: none;
  }

  ${p => p.theme.breakpoint.tablet} {
    flex-wrap: wrap;
  }

  ${p => p.theme.breakpoint.mobile} {
    flex-direction: column;
    width: auto;
  }
`;

const ProfileSection = styled.div`
  display: flex;
  white-space: normal;
  column-gap: 5px;
  flex-wrap: wrap;
`;

const ProfileColumnSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 5px;
  row-gap: 2px;
  line-height: 1.75em;

  .linkText {
    overflow: visible;
  }

  .linkIconContainer {
    vertical-align: middle;
  }
`;

const InlineIcon = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 1;
`;

const makeCustomSearchUrl = organisation =>
  `http://www.google.com/search?q=${organisation} site:linkedin.com`;

const Profile = ({
  organisationName,
  organisationCity,
  organisationCountry,
  organisationCategory,
  superOrganisationSizeRangeLowerBound,
  superOrganisationSizeRangeUpperBound,
  superOrganisationId,
  // superOrganisationRevenue,
  superOrganisationFoundedYear,
  superOrganisationLinkedIn,
  superOrganisationLogoUrl,
  superOrganisationAcquisitions,
  superOrganisationPreviousName,
  superOrganisationParentId,
  superOrganisationParentName,
  latestFundingType,
}) => {
  const dispatch = useDispatch();
  const scrollableProfile = useRef(null);
  const orgProfileLink = useOrgProfileLink();

  const setOrgProfileView = () => {
    dispatch(setViewAndResetState(ORGANISATIONPEOPLEVIEW));
  };

  useHorizontalWheel(scrollableProfile);

  return (
    <ProfileContainer>
      <ProfileContainerInner>
        <ProfileSection style={{ alignItems: 'center' }}>
          <InfoGroup>
            <Link
              size='large'
              to={orgProfileLink(superOrganisationId)}
              showIconHint
              style={{ marginBottom: '0px' }}
              onClick={() => setOrgProfileView()}
            >
              {organisationName}
            </Link>
            {latestFundingType && (
              <InlineIcon>
                <FundingTypeIcon fundingType={latestFundingType} />
              </InlineIcon>
            )}
            {superOrganisationAcquisitions && (
              <InlineIcon>
                <AcquiredIcon
                  size='lg'
                  acquisitions={superOrganisationAcquisitions}
                />
              </InlineIcon>
            )}
            {superOrganisationParentId && (
              <InlineIcon>
                <SubsidiaryIcon
                  superOrganisationParentId={superOrganisationParentId}
                  superOrganisationParentName={superOrganisationParentName}
                />
              </InlineIcon>
            )}
            {superOrganisationPreviousName && (
              <InlineIcon>
                <RebrandIcon previousName={superOrganisationPreviousName} />
              </InlineIcon>
            )}
            <InlineIcon>
              <SocialMediaLink
                network={{
                  type: 'linkedin',
                  displayName: 'LinkedIn',
                }}
                title={
                  superOrganisationLinkedIn
                    ? 'View on LinkedIn'
                    : 'Search for LinkedIn'
                }
                url={
                  superOrganisationLinkedIn
                    ? `https://${superOrganisationLinkedIn}`
                    : makeCustomSearchUrl(organisationName)
                }
                isMuted={superOrganisationLinkedIn ? false : true}
                logDescription={{
                  entityType: 'Organisation',
                  location: 'OrganisationSearch',
                }}
              />
            </InlineIcon>
          </InfoGroup>
        </ProfileSection>
        <ProfileSection
          ref={scrollableProfile}
          className='horizontalScrollable'
          style={{ marginRight: '15px', columnGap: 15, rowGap: 0 }}
        >
          <ProfileColumnSection>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <IconText
                value={organisationCategory}
                icon='category'
                style={{ marginRight: '5px' }}
              />
              <Actions
                organisation={superOrganisationId}
                modalTitle={'Report Organisation Category'}
                isAiIcon={true}
                placeholder={'Organisation Category Suggestion'}
              />
            </div>
            {(superOrganisationSizeRangeLowerBound ||
              superOrganisationSizeRangeUpperBound) && (
              <Tooltip
                title={
                  'This number represents the count of employees at the company'
                }
              >
                <span>
                  <IconText
                    value={formatRange(
                      superOrganisationSizeRangeLowerBound,
                      superOrganisationSizeRangeUpperBound
                    )}
                    icon='user'
                  />
                </span>
              </Tooltip>
            )}
            {superOrganisationFoundedYear && (
              <Tooltip title={'This represents when the company was founded'}>
                <span>
                  <IconText
                    value={superOrganisationFoundedYear}
                    icon='rocket'
                  />
                </span>
              </Tooltip>
            )}
          </ProfileColumnSection>
          <ProfileColumnSection>
            <IconText value={organisationCity} icon='map' />
            <IconText value={organisationCountry} icon='globe' />
          </ProfileColumnSection>
        </ProfileSection>
      </ProfileContainerInner>
    </ProfileContainer>
  );
};

export default Profile;
